import { inject, observer } from "mobx-react";
import React from "react";
import { compose } from "recompose";
import appConstants from "../../../../../../appConstants";
import { withUserProfile } from "../../../../../../userProfile/userProfileContext";
import DataGrid from "../../../../../dataGrid/index";
import StatusBar from "../../../../../dataGrid/StatusBar";
import { PICKUP_STATUS } from "../../constants";
import CustomConfirmationDialog from "../../DriverPopUp/common/CustomConfirmationDialog";
import { unassignPickup } from "../../utils/pickupActions";
import PickupDialog from "../PickupInfoPopup/PickupDialog";
import PickupManagement from "../PickupManagement";
import { PICKUP_MANAGEMENT_MODES } from "../PickupManagement/constants";
import ConfirmationDialogContent from "../PickupManagement/VoidPickupInfo/ConfirmationDialogContent";
import PickupsGridConfig from "./GridConfig";
import { filterPickup, hasPriority } from "../../filterUtils";
import { getPickupTypes , getSpecialNeeds } from "../../../../utils";
import { autorun } from "mobx";
import { isEqual } from "lodash/lang";
import { PICKUPS_GRID_FILTERS } from "./Filter/DEFAULT_constants";
import Events from "../../../../../../utilities/EventEmitter";
import { withAppConfig } from "../../../../../../appConfigContext";
import { getDuplicatePickupsByPickupId, isPossibleMergeFilterApplied } from "../../../utils/utils";
import { isMergePickupMode } from "../PickupManagement/utils";

@inject("DispatchStore")
@observer
class PickupsGrid extends React.Component {
  constructor(props) {
    super(props);
    this.gridConfig = new PickupsGridConfig({
      userConfig : this.props.userData.userProfile?.columnPref,      
      showStaticRoute : this.props.configJSON?.showStaticRoute,
      featureFlags: {
        enableColumnShortForm: this.props.configJSON?.enableColumnShortForm,
      },
    });

    this.gridHeaderId = `gridHeader_${this.props.glContainer._config.id}`;
    this.gridApi = {};
    this.state = {
      quickFilterText: "",
      open: false,
      headerFilter: this.props.DispatchStore.pickupsFilter,
      panelFilter: PICKUP_STATUS.unassigned,
      mode: "",
      selectedPossibleMergePickupId: null,
    };
    this.priorityShipmentList = this.props.DispatchStore.priorityAllShipmentList;
    this.gridContext = {
      pickupTypeOptions: getPickupTypes(
        this.props.DispatchStore.priorityShipmentList
      ),
      specialNeedsOptions: getSpecialNeeds(this.priorityShipmentList),
      terminalOffset: this.props.DispatchStore.terminalOffset,
      handlePossibleMergeClick: this.handlePossibleMergeClick,
      getSelectedPickupsDuplicate: this.getSelectedPickupsDuplicate,
    };
    this.pickupsStatusUpdateTimer = null;
  }
  componentDidMount() {
    this.props.glEventHub.on("pickupManagement", this.openPickupManagement);
    this.props.glEventHub.on(this.gridHeaderId, this.onSearchTextChanged);
    Events.on("RerenderDuplicatePickups", this.refreshPossibleMergeIcon);
    this.props.glEventHub.on(
      `${this.gridHeaderId}_selectFilterSelected`,
      this.headerFilterChanged,
    );
    this.props.glEventHub.on(
      appConstants.Event.PICKUP_SELECTED_MAP,
      this.onPickupSelectedOnMap,
    );
    this.autorunPriorityShipmentList = autorun(() => {
      if (
        !isEqual(
          this.priorityShipmentList,
          this.props.DispatchStore.priorityAllShipmentList
        )
      ) {
        this.priorityShipmentList = this.props.DispatchStore.priorityAllShipmentList;
        this.gridContext = {
          terminalOffset: this.props.DispatchStore.terminalOffset,
          pickupTypeOptions: getPickupTypes(
            this.props.DispatchStore.priorityShipmentList
          ),
          specialNeedsOptions: getSpecialNeeds(this.priorityShipmentList),
          handlePossibleMergeClick: this.handlePossibleMergeClick,
          getSelectedPickupsDuplicate: this.getSelectedPickupsDuplicate,
        };
      }
    });
  }

  componentWillUnmount() {
    this.props.glEventHub.off("pickupManagement", this.openPickupManagement);
    this.props.glEventHub.off(this.gridHeaderId, this.onSearchTextChanged);
    this.props.glEventHub.off(
      `${this.gridHeaderId}_selectFilterSelected`,
      this.headerFilterChanged,
    );
    this.props.glEventHub.off(
      appConstants.Event.PICKUP_SELECTED_MAP,
      this.onPickupSelectedOnMap,
    );
    this.autorunPriorityShipmentList?.();
    clearInterval(this.pickupsStatusUpdateTimer);
    this.pickupsStatusUpdateTimer = null;
    Events.off("RerenderDuplicatePickups", this.refreshPossibleMergeIcon)
  }

  startPickupsRefreshInterval = () => {
    this.pickupsStatusUpdateTimer = setInterval(()=>{
       this.gridApi.refreshCells({ columns: ["pickupNumber"], force: true });
    }, appConstants.ONE_MINUTE_TIME_OUT)
  }

  getSelectedPickupsDuplicate = (pickupId) => {
    return getDuplicatePickupsByPickupId(this.props.DispatchStore.duplicatePickups, pickupId);
  }


  handlePossibleMergeClick =
    (selectedPickupId, duplicatePickups = []) =>
    () => {
      this.props.DispatchStore.updateSelectedPickups(duplicatePickups);
      this.setState({
        mode: PICKUP_MANAGEMENT_MODES.MERGE_PICKUP,
        selectedPossibleMergePickupId: selectedPickupId,
      });
    };

  refreshPossibleMergeIcon = () => {
    this.gridApi.refreshCells({ columns: ["isMergedPickup"], force: true });
  };

  openPickupManagement = (eventData) => {
    this.setState({
      mode: eventData.action,
      ...(isMergePickupMode(eventData.action) && {
        selectedPossibleMergePickupId: null, /* reset possible merge pickupId if model opened through header icon */
      }),
    });
  };

  closePickupManagement = () => {
    this.setState({
      mode: "",
    });
  };

  onSelectionChanged = (event) => {
    //this method is to be updated according to final implementation
    if (!this.gridApi) {
      return;
    }
    const selectedPickupIds = this.gridApi
      .getSelectedRows()
      .map((pickup) => pickup.pickupId);

    this.props.glEventHub.emit("UpdateDispatchStore", "updateSelectedPickups", [
      selectedPickupIds,
    ]);
    if (this.pickupSelectedOnGrid) {
      this.props.glEventHub.emit(appConstants.Event.PICKUP_SELECTED_GRID, {
        pickupsData: this.props.DispatchStore.selectedPickupsList,
      });
    }
    this.props.glEventHub.emit(
      appConstants.Event.SELECT_PICKUP_ROUTES,
      event?.refreshDriverSelection,
    );
    this.updatePickupManagementIconStatus();
  };

  onSearchTextChanged = (searchText) => {
    this.setState({
      quickFilterText: searchText,
    });
  };

  onCellClicked = () => {
    this.pickupSelectedOnGrid = true;
  };

  onRowDataUpdated = () => {
    this.gridApi.refreshCells({ columns: ["pickupNumber"], force: true });
    this.updatePickupManagementIconStatus();
  }

  onPickupSelectedOnMap = (
    selectedPickupIdList,
    refreshDriverSelection = true,
  ) => {
    if (this.gridApi) {
      this.pickupSelectedOnGrid = false;
      this.gridApi.forEachNode((node) => {
        if (selectedPickupIdList.includes(node.data.pickupId)) {
          node.setSelected(true, false, true);
          this.gridApi.ensureIndexVisible(node.rowIndex);
        } else {
          node.setSelected(false, false, true);
        }
      });
      this.gridApi.dispatchEvent({
        type: "selectionChanged",
        api: this.gridApi,
        refreshDriverSelection: refreshDriverSelection,
      });
    }
  };

  onGridReady = ({ api }) => {
    this.gridApi = api;
    this.selectPickups();
    this.updatePickupManagementIconStatus();
    this.startPickupsRefreshInterval();
  };

  selectPickups = () => {
    if (this.gridApi?.forEachNode) {
      this.gridApi.forEachNode((node) => {
        if (
          this.props.DispatchStore.selectedPickupIdList.includes(
            node.data.pickupId.toLowerCase(),
          )
        ) {
          node.setSelected(true, false, true); // props are isSelected, clearOtherSelection, suppressFinishActions
          this.gridApi.ensureIndexVisible(node.rowIndex);
        } else {
          // disabling supress finish actions calls onSelectionChanged event for each selection
          node.setSelected(false, false, true); // but we want single call of onSelectionChanged for multiselection , behaviour observed through user action
        } // so we suppress the call from setSelected and call it explicitly after loop completes
      });
    }
  };

  updatePickupManagementIconStatus = () => {
    const selectedPickups = this.gridApi.getSelectedRows();
    this.props.glEventHub.emit(
      `${this.gridHeaderId}_updatePickupManagementIconStatus`,
      {
        selectedPickups,
        terminalOffset: this.props.DispatchStore.terminalOffset
      }
    );
  };

  constructStatusBar = (filteredData) => {
    return [
      {
        label: filteredData.length + " Pickups",
        filterCondition: PICKUP_STATUS.allPickups,
      },
      {
        label:
          filteredData.filter((pickup) =>
          hasPriority(
            pickup.priority,
            this.props.DispatchStore.priorityShipmentList
          )
          ).length + " Guaranteed",
        filterCondition: "guaranteed",
      },
      {
        label:
          filteredData.filter((pickup) => pickup.isSwapPickup).length +
          " Swaps",
        filterCondition: "swaps",
      },
    ];
  };

  onDoubleClicked = () => {
    this.setState({
      open: true,
    });
  };

  headerFilterChanged = (newValue) => {
    this.gridApi?.deselectAll?.(); // clear pickup selections when grid filter is changed
    this.props.glEventHub.emit(appConstants.Event.PICKUP_SELECTED_GRID, {
      pickupsData: [],
    });
    this.setState(
      {
        headerFilter: newValue,
      },
      () => this.gridApi.onFilterChanged?.(),
    );
    this.props.glEventHub.emit("pickupsGridFilterChanged", newValue);
  };

  panelFilterChanged = (filter) => {
    this.setState(
      {
        panelFilter: filter,
      },
      () => this.gridApi.onFilterChanged?.(),
    );
  };

  doesExternalFilterPass = (node) => {
    const isPossibleMergeFilter = isPossibleMergeFilterApplied(
      this.state.headerFilter.value
    );
    let doesFilterPass;
    doesFilterPass = filterPickup({
      pickup: node.data,
      ...(isPossibleMergeFilter && {
        duplicatePickups: getDuplicatePickupsByPickupId(
          this.props.DispatchStore.duplicatePickups,
          node.data.pickupId
        ),
      }),
      pickupFilter: this.state.headerFilter,
      terminalOffset: this.props.DispatchStore.terminalOffset,
    });

    if (this.state.panelFilter === "guaranteed") {
      doesFilterPass =
        doesFilterPass &&
        hasPriority(
          node.data.priority,
          this.props.DispatchStore.priorityShipmentList
        )
    } else if (this.state.panelFilter === "swaps") {
      doesFilterPass = doesFilterPass && node.data.isSwapPickup;
    } else {
      doesFilterPass = Boolean(doesFilterPass);
    }
    return doesFilterPass;
  };

  handleClose = () => {
    this.setState({
      open: false,
    });
  };

  getSelectedPickupData = (selectedPickupId) => {
    const { DispatchStore } = this.props;
    return DispatchStore.getPickupData(selectedPickupId);
  };

  unassignPickupFromDriver = () => {
    const { pickupId, driverId } =
      this.props.DispatchStore.selectedPickupIdList.length > 0 &&
      this.getSelectedPickupData(
        this.props.DispatchStore.selectedPickupIdList[0]
      );

    unassignPickup({
      pickupIds: [pickupId],
      driverId: driverId,
      terminalCode: this.props.userData.userProfile.terminal.code,
      glEventHub: this.props.glEventHub,
    })
      .then(() => {
        this.closePickupManagement();
      })
      .catch(() => {
        this.props.glEventHub.emit("DispatchEvents", "HideLoaderDialog", []);
      });
  };

  getSelectedPickupsByIds = () => {
    let duplicatePickupIds = this.props.DispatchStore.selectedPickupIdList;
    if (this.state.selectedPossibleMergePickupId) {
      duplicatePickupIds = getDuplicatePickupsByPickupId(
        this.props.DispatchStore.duplicatePickups,
        this.state.selectedPossibleMergePickupId
      );
    }
    return duplicatePickupIds.map(
      (pickupId) => this.getSelectedPickupData(pickupId)
    );
  }

  getPickupManagementDetailsByMode = (selectedPickupData) => {
    if (this.state.mode === PICKUP_MANAGEMENT_MODES.CREATE_PICKUP) {
      return {
        title: "Add New Pickup",
        pickupData: null,
      };
    }
    if (this.state.mode === PICKUP_MANAGEMENT_MODES.MERGE_PICKUP) {
      const selectedPickups = this.getSelectedPickupsByIds();
      return {
        title: "Merge Pickup",
        pickupData: selectedPickups.filter(Boolean),
      };
    }
    return {
      title: `${selectedPickupData.shipperName} (${selectedPickupData.pickupNumber})`,
      pickupData: selectedPickupData,
    };
  };

  getGridData = () => {
    const { pickupsFilter, pickupsList, duplicatePickups } = this.props.DispatchStore;
    if (pickupsFilter.value === PICKUPS_GRID_FILTERS.possibleMerge) {
      const groupedPickups = pickupsList.reduce((acc, pickup) => {
        getDuplicatePickupsByPickupId(duplicatePickups, pickup.pickupId).forEach((id) => {
          if (!acc.some((p) => p.pickupId.toLowerCase() === id.toLowerCase())) {
            const duplicatePickup = pickupsList.find(
              (p) => p.pickupId.toLowerCase() === id.toLowerCase()
            );
            if (duplicatePickup) {
              acc.push(duplicatePickup);
            }
          }
        });
        return acc;
      }, []);
      return groupedPickups.length > 0 ? groupedPickups : "";
    } else {
      return pickupsList.slice();
    }
  };

  render() {
    const { mode, selectedPossibleMergePickupId } = this.state;
    const pickups = this.getGridData();
    const selectedPickupData =
      this.props.DispatchStore.selectedPickupIdList.length > 0 &&
      this.getSelectedPickupData(
        this.props.DispatchStore.selectedPickupIdList[0]
      );
    
    return (
      <React.Fragment>
        <DataGrid
          defaultColDef={this.gridConfig.config.defaultColDef}
          columnDefs={this.gridConfig.config.columns}
          columnTypes={this.gridConfig.config.columnTypes}
          config={this.gridConfig.config}
          rowSelection={this.gridConfig.config.rowSelection}
          rowDeselection={this.gridConfig.config.rowDeselection}
          rowData={pickups}
          onCellClicked={this.onCellClicked}
          onGridReady={this.onGridReady}
          onSelectionChanged={this.onSelectionChanged}
          onCellDoubleClicked={this.onDoubleClicked}
          panelLabel={(filteredData) => (
            <StatusBar
              data={this.constructStatusBar(
                filteredData.filter?.((data) =>
                  this.doesExternalFilterPass({ data })
                ) ?? []
              )}
              filterGridData={this.panelFilterChanged}
            />
          )}
          isExternalFilterPresent={() =>
            Boolean(this.state.headerFilter.value || this.state.panelFilter)
          }
          doesExternalFilterPass={this.doesExternalFilterPass}
          layoutContainer={this.props.glContainer}
          quickFilterText={this.state.quickFilterText}
          deltaRowDataMode={true}
          onRowDataUpdated={this.onRowDataUpdated}
          getRowNodeId={(data) => data.pickupId}
          showErrorMessage={this.props.DispatchStore.pickupsGridMessage}
          context={this.gridContext}
        />
        {this.state.open && selectedPickupData && (
          <PickupDialog
            open={this.state.open}
            handleClose={this.handleClose}
            pickupinfo={selectedPickupData}
          />
        )}
        {this.state.mode &&
          (mode === PICKUP_MANAGEMENT_MODES.VOID_PICKUP &&
          selectedPickupData?.driverId ? (
            <CustomConfirmationDialog
              title="Assigned pickup selected to void."
              cancelButtonLabel="Cancel"
              confirmButtonLabel="Unassign"
              onConfirm={this.unassignPickupFromDriver}
              onCancel={this.closePickupManagement}
            >
              <ConfirmationDialogContent />
            </CustomConfirmationDialog>
          ) : (
            <PickupManagement
              handleClose={this.closePickupManagement}
              mode={this.state.mode}
              glEventHub={this.props.glEventHub}
              {...this.getPickupManagementDetailsByMode(selectedPickupData)}
              selectedPossibleMergePickupId={selectedPossibleMergePickupId}
              open
            />
          ))}
      </React.Fragment>
    );
  }
}
export default compose(
  withUserProfile,
  withAppConfig
)(PickupsGrid);
